/**
 * WorldList — the home view. Shows the 10 worlds; cards for paid worlds
 * are marked locked when the user hasn't purchased the full unlock.
 */
import type { Locale, World } from './types';
import { getSolvedSet } from './storage';

const FREE_WORLD_IDS = new Set(['threshold', 'garden', 'kitchen']);

interface Props {
  readonly locale: Locale;
  readonly worlds: readonly World[];
  readonly unlocked: boolean;
  readonly onOpenWorld: (world: World, ladderIdx: number) => void;
  readonly onOpenSettings: () => void;
}

export function WorldList(props: Props) {
  const { locale, worlds, unlocked, onOpenWorld, onOpenSettings } = props;
  const solvedSet = getSolvedSet();

  return (
    <section id="worldList" className="view">
      <header className="appHeader">
        <div className="titleBlock">
          <h1>Doublet</h1>
          <div className="sub">A quiet word ladder · Carroll, 1879</div>
        </div>
        <button className="iconBtn ghost" aria-label="Settings" onClick={onOpenSettings}>⋯</button>
      </header>

      <div className="cards">
        {worlds.length === 0 ? (
          <div style={{
            color: 'var(--muted)',
            fontStyle: 'italic',
            textAlign: 'center',
            padding: '2rem 0',
          }}>
            No ladders for {locale.toUpperCase()} yet — try a different language.
          </div>
        ) : (
          worlds.map((world) => {
            const locked = !FREE_WORLD_IDS.has(world.worldId) && !unlocked;
            const solvedHere = world.ladders.reduce(
              (acc, _, i) => acc + (solvedSet.has(`${locale}|${world.worldId}|${i}`) ? 1 : 0),
              0
            );
            const className = 'card' + (locked ? ' locked' : '');
            const lockTag = locked ? ' · locked' : '';
            return (
              <button
                key={world.worldId}
                className={className}
                onClick={() => onOpenWorld(world, 0)}
              >
                <h2>{world.themeName}</h2>
                <div className="desc">{world.description ?? ''}</div>
                <div className="meta">
                  {world.ladders.length} ladder{world.ladders.length === 1 ? '' : 's'}
                  {' · '}
                  {solvedHere} solved
                  {' · '}
                  {world.stepRange[0]}–{world.stepRange[1]} swaps
                  {lockTag}
                </div>
              </button>
            );
          })
        )}
      </div>
    </section>
  );
}
